import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSiteSettings, whatsappLink } from "@/lib/SiteSettingsContext";
import { toast, Toaster } from "sonner";

export default function Contact() {
  const { settings } = useSiteSettings();
  const [content, setContent] = useState({
    hero_eyebrow: "İletişim",
    hero_title: "Sizinle tanışmak için sabırsızız",
    info_title: "İletişim Bilgileri",
    working_hours: "Pazartesi - Cumartesi · 08:30 - 18:00",
    form_title: "Bize yazın",
    form_subtitle: "Aşağıdaki formu doldurarak bize ulaşabilirsiniz. En kısa sürede geri dönüş yapacağız.",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/page-content/contact").then(({ data }) => setContent(data));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Lütfen ad, e-posta ve mesaj alanlarını doldurun.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız iletildi! En kısa sürede dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bir hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div data-testid="contact-page">
      <Toaster position="top-right" />
      <section className="bg-[#0A2540] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
            {content.hero_eyebrow}
          </div>
          <h1 className="heading-font text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">
            {content.hero_title}
          </h1>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <h2 className="heading-font text-2xl text-[#0A2540] mb-6">{content.info_title}</h2>
            <div className="space-y-5">
              <div className="border-l-2 border-[#0066FF] pl-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
                  <MapPin size={14} /> Adres
                </div>
                <div className="text-[#0A2540] font-medium" data-testid="contact-address">
                  {settings.address}
                </div>
              </div>
              <div className="border-l-2 border-[#0066FF] pl-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
                  <Phone size={14} /> Telefon
                </div>
                <a
                  href={`tel:${settings.phone?.replace(/\s/g, "")}`}
                  className="text-[#0A2540] font-medium hover:text-[#0066FF]"
                  data-testid="contact-phone"
                >
                  {settings.phone}
                </a>
              </div>
              {settings.email && (
                <div className="border-l-2 border-[#0066FF] pl-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
                    <Mail size={14} /> E-posta
                  </div>
                  <a
                    href={`mailto:${settings.email}`}
                    className="text-[#0A2540] font-medium hover:text-[#0066FF]"
                    data-testid="contact-email"
                  >
                    {settings.email}
                  </a>
                </div>
              )}
              <div className="border-l-2 border-[#0066FF] pl-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
                  <Clock size={14} /> Çalışma Saatleri
                </div>
                <div className="text-[#0A2540] font-medium">
                  {content.working_hours}
                </div>
              </div>
            </div>

            <a
              href={whatsappLink(settings.whatsapp)}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ea855] text-white px-6 py-4 font-semibold transition-colors w-full"
              data-testid="contact-whatsapp-cta"
            >
              WhatsApp ile Direkt Yazın
            </a>

            <div className="mt-8 aspect-video bg-slate-100 overflow-hidden">
              <iframe
                title="Konum"
                src="https://maps.google.com/maps?q=Aksaray+Merkez+Hac%C4%B1lar+Harman%C4%B1+50.+Cadde&z=14&output=embed"
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="border border-slate-200 p-8 md:p-10">
              <h2 className="heading-font text-2xl text-[#0A2540] mb-2">{content.form_title}</h2>
              <p className="text-sm text-slate-600 mb-8">
                {content.form_subtitle}
              </p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none"
                      data-testid="contact-input-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none"
                      data-testid="contact-input-email"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                      Firma
                    </label>
                    <input
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                    Konu
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none"
                    data-testid="contact-input-subject"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                    Mesajınız *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none resize-none"
                    data-testid="contact-input-message"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-4 bg-[#0A2540] hover:bg-[#0066FF] disabled:opacity-60 text-white font-semibold transition-colors"
                  data-testid="contact-submit"
                >
                  {submitting ? "Gönderiliyor..." : "Mesajı Gönder"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
